/* Interface publique française - Version production */
import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Bike, Car, Palmtree, MousePointerClick, CalendarCheck, Smile, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/Header';
import { SEO } from '@/components/SEO';
import { Footer } from '@/components/Footer';
import { Input } from '@/components/ui/input';
import { currentUrlLang, pathInLang } from '@/i18n/urlLanguage';

// Images sélectionnées pour le thème Thaïlande - Photos réalistes de Phuket
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=1600&q=80',
  scooter: 'https://customer-assets.emergentagent.com/job_journey-link-13/artifacts/j9icv8bt_Image.png',
  transfer: 'https://customer-assets.emergentagent.com/job_journey-link-13/artifacts/b4c12i2f_43c5c7f77ee9b523f398bbce78b1e3f7dc6936de6dc6721b3386f59e4b3b7596.png',
  excursion: 'https://customer-assets.emergentagent.com/job_journey-link-13/artifacts/jbxduxew_97306c6459ad838da17edf25ce6f5253c0f0d65e590ac58da2318a74a2e13e1e.png',
};

export const LandingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleSearch = (e) => {
    e?.preventDefault?.();
    // Router navigation: keeps the /en prefix on English URLs.
    navigate(`/services${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`);
  };

  const services = [
    {
      id: 'scooter',
      title: t('landing.services.scooterTitle'),
      description: t('landing.services.scooterDescription'),
      image: IMAGES.scooter,
      icon: Bike,
      cta: t('landing.services.scooterCta'),
      link: '/scooters',
    },
    {
      id: 'transfer',
      title: t('landing.services.transportTitle'),
      description: t('landing.services.transportDescription'),
      image: IMAGES.transfer,
      icon: Car,
      cta: t('landing.services.transportCta'),
      link: '/transport',
    },
    {
      id: 'excursion',
      title: t('landing.services.excursionTitle'),
      description: t('landing.services.excursionDescription'),
      image: IMAGES.excursion,
      icon: Palmtree,
      cta: t('landing.services.excursionCta'),
      link: '/services?category=excursion',
    },
  ];

  const steps = [
    {
      num: '01',
      icon: MousePointerClick,
      title: t('landing.howItWorks.step1Title'),
      body: t('landing.howItWorks.step1Body'),
    },
    {
      num: '02',
      icon: CalendarCheck,
      title: t('landing.howItWorks.step2Title'),
      body: t('landing.howItWorks.step2Body'),
    },
    {
      num: '03',
      icon: Smile,
      title: t('landing.howItWorks.step3Title'),
      body: t('landing.howItWorks.step3Body'),
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SEO title={t('seo.home.title')} description={t('seo.home.description')} canonical="https://ridalo.co/" />
      <Header />

      {/* Mobile Homepage — Marketplace Entrance */}
      <section className="block md:hidden pt-[4.5rem] pb-6 bg-white" data-testid="hero-mobile">
        <div className="max-w-md mx-auto px-4">
          {/* Short hook: what Ridalo is and what can be booked (the h1 lives in the desktop hero). */}
          <div className="text-center" data-testid="hero-mobile-hook">
            <p className="text-2xl font-bold tracking-tight text-primary leading-tight whitespace-nowrap">{t('landing.heroMobile.hook')}</p>
            <p className="mt-0.5 text-sm text-ink-light whitespace-nowrap">{t('landing.heroMobile.hookSub')}</p>
          </div>
          {/* Service Cards — Visual Marketplace Entry */}
          <div className="space-y-3 mt-3">
            {/* SCOOTERS */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Link
                to="/scooters"
                className="block rounded-2xl overflow-hidden shadow-lg transform transition-all active:scale-[0.98] hover:shadow-xl"
                data-testid="hero-mobile-scooters"
              >
                <div className="relative h-48">
                  <img
                    src={IMAGES.scooter}
                    alt={t('landing.heroMobile.scootersTitle')}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h2 className="text-2xl font-bold text-white mb-1">{t('landing.heroMobile.scootersTitle')}</h2>
                    <p className="text-sm text-white/90">{t('landing.heroMobile.scootersSubtitle')}</p>
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* TRANSPORT */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Link
                to="/transport"
                className="block rounded-2xl overflow-hidden shadow-lg transform transition-all active:scale-[0.98] hover:shadow-xl"
                data-testid="hero-mobile-transport"
              >
                <div className="relative h-48">
                  <img
                    src={IMAGES.transfer}
                    alt={t('landing.heroMobile.transportTitle')}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h2 className="text-2xl font-bold text-white mb-1">{t('landing.heroMobile.transportTitle')}</h2>
                    <p className="text-sm text-white/90">{t('landing.heroMobile.transportSubtitle')}</p>
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* EXCURSIONS */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Link
                to="/services?category=excursion"
                className="block rounded-2xl overflow-hidden shadow-lg transform transition-all active:scale-[0.98] hover:shadow-xl"
                data-testid="hero-mobile-excursions"
              >
                <div className="relative h-48">
                  <img
                    src={IMAGES.excursion}
                    alt={t('landing.heroMobile.excursionsTitle')}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-accent/90 via-accent/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h2 className="text-2xl font-bold text-white mb-1">{t('landing.heroMobile.excursionsTitle')}</h2>
                    <p className="text-sm text-white/90">{t('landing.heroMobile.excursionsSubtitle')}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hero Section — Desktop (unchanged) */}
      <section className="hidden md:block relative pt-20 lg:pt-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 lg:pt-10">
          <div className="relative rounded-3xl overflow-hidden shadow-card">
            <div className="relative h-[520px] sm:h-[560px] lg:h-[600px]">
              <img
                src={IMAGES.hero}
                alt={t('landing.hero.imageAlt')}
                fetchPriority="high"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/50 to-transparent"></div>

              <div className="relative z-10 h-full flex items-center">
                <div className="px-6 sm:px-10 lg:px-16 max-w-3xl">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-4">
                      {t('landing.hero.titleLine1')}<br />
                      {t('landing.hero.titleLine2')}<br />
                      <span className="text-accent">{t('landing.hero.titleLine3')}</span>
                    </h1>
                    <p className="text-base sm:text-lg text-white/90 mb-8 max-w-xl">
                      {t('landing.hero.subtitle')}
                    </p>

                    <form onSubmit={handleSearch} className="bg-white rounded-2xl p-2 shadow-xl max-w-2xl flex flex-col sm:flex-row gap-2" data-testid="hero-search-box">
                      <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          type="text"
                          placeholder={t('landing.hero.searchPlaceholder')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-12 pr-4 py-3 border-0 focus:ring-0 text-base"
                          data-testid="hero-search-input"
                        />
                      </div>
                      <button type="submit" className="btn-primary whitespace-nowrap" data-testid="hero-search-button">
                        <Search className="w-4 h-4 mr-2" />
                        {t('landing.hero.searchButton')}
                      </button>
                    </form>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Grandes cartes cliquables — Hidden on mobile (duplicates new mobile hero) */}
      <section className="hidden md:block py-12 lg:py-20" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 lg:mb-14"
          >
            <h2 className="section-title">{t('landing.services.sectionTitle')}</h2>
            <p className="text-base sm:text-lg text-ink-light mt-4">{t('landing.services.sectionSubtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={service.link}
                  className="group relative block rounded-3xl overflow-hidden shadow-card hover:shadow-cardHover transition-all duration-500 hover:-translate-y-2 h-[420px] sm:h-[460px] lg:h-[520px]"
                  data-testid={`service-card-${service.id}`}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/20"></div>

                  <div className="absolute top-5 right-5 icon-circle shadow-lg">
                    <service.icon className="w-7 h-7 text-white" strokeWidth={2} />
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 bg-gradient-to-t from-primary via-primary/95 to-transparent pt-16">
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 leading-tight text-white">
                      {service.title}
                    </h3>
                    <p className="text-sm sm:text-base text-white mb-5 opacity-95">
                      {service.description}
                    </p>
                    <div className="inline-flex items-center gap-2 bg-accent text-white font-semibold text-sm sm:text-base px-6 py-3 rounded-full shadow-md group-hover:bg-accent-hover group-hover:gap-3 transition-all">
                      {service.cta}
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local page: real links in the page (also in the initial HTML, scripts/seo-static.js) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pb-12 text-center" data-testid="home-local-links">
        <Link
          to={pathInLang(currentUrlLang() || 'fr', '/location-scooter-patong')}
          className="inline-flex items-center gap-1 text-sm sm:text-base font-medium text-accent hover:underline"
          data-testid="home-local-patong-link"
        >
          <MapPin className="w-4 h-4 shrink-0" aria-hidden="true" />
          {t('scooters.localPatongLink')}
        </Link>
      </div>

      {/* Comment ça marche ? */}
      <section className="pb-12 lg:pb-20 pt-4 lg:pt-6 bg-slate-50/60" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 lg:mb-14"
          >
            <h2 className="section-title">{t('landing.howItWorks.sectionTitle')}</h2>
            <p className="text-base sm:text-lg text-ink-light mt-4">
              {t('landing.howItWorks.sectionSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative bg-white rounded-3xl p-8 shadow-card hover:shadow-cardHover transition-all duration-500 hover:-translate-y-1"
                data-testid={`how-step-${step.num}`}
              >
                <span
                  aria-hidden="true"
                  className="absolute top-4 right-6 text-5xl sm:text-6xl font-bold text-slate-100 select-none leading-none"
                >
                  {step.num}
                </span>
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mb-5">
                    <step.icon className="w-7 h-7" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-primary mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-ink-light leading-relaxed">
                    {step.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};
